/**
 * Lane layout for the new Grid (#1766, decision 5): ROUTE ORDER, READABLE
 * WIDTH, APPROXIMATE TIME.
 *
 * - Cards go left to right in start-time order — the route.
 * - A card's width is its real duration on the clock, but never narrower than
 *   `minWidthPx`, so a 30-minute test still shows its site name.
 * - A card sits at its clock position unless the card before it is in the way;
 *   then it is pushed right to sit just after it. So position is the time
 *   when there is room, and the order when there isn't.
 * - Real open time between two stops (≥ `gapMinutes`) is returned as a gap, so
 *   the board can draw it.
 *
 * Returns the same `PositionedBlock` shape the classic swimlane uses, so the
 * shared drop math (`landingForDrop`) works on it unchanged.
 */
import { DEFAULT_VISIT_MINUTES, MIN_VISIT_MINUTES, type DispatchJob } from "../dispatch-types";
import type { PositionedBlock } from "../day-swimlane";

export const NEW_GRID_PX_PER_HOUR = 132;
export const NEW_GRID_MIN_CARD_PX = 132;
export const NEW_GRID_CARD_GAP_PX = 6;
/** Open time shorter than this between two stops is just driving. */
export const NEW_GRID_GAP_MINUTES = 45;

export interface LaneGap {
    leftPx: number;
    widthPx: number;
    minutes: number;
}

export interface LaneLayout {
    blocks: PositionedBlock[];
    gaps: LaneGap[];
    /** Right edge of the last card, px. */
    contentRight: number;
    /** Jobs before / after the clock window — counted, never dropped silently. */
    beforeCount: number;
    afterCount: number;
}

export function visitMinutes(
    job: Pick<DispatchJob, "scheduled_start" | "scheduled_end" | "estimated_duration_minutes">,
): number {
    const startMs = job.scheduled_start ? Date.parse(job.scheduled_start) : NaN;
    const endMs = job.scheduled_end ? Date.parse(job.scheduled_end) : NaN;
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
        return Math.max(MIN_VISIT_MINUTES, (endMs - startMs) / 60_000);
    }
    return Math.max(MIN_VISIT_MINUTES, job.estimated_duration_minutes ?? DEFAULT_VISIT_MINUTES);
}

export function cardWidthPx(minutes: number, pxPerMin: number, minWidthPx = NEW_GRID_MIN_CARD_PX): number {
    return Math.max(minWidthPx, Math.round(minutes * pxPerMin) - NEW_GRID_CARD_GAP_PX);
}

export function layoutLane(
    jobs: DispatchJob[],
    dayStartMs: number,
    windowHours: number,
    pxPerMin: number,
    opts: { minWidthPx?: number; gapMinutes?: number } = {},
): LaneLayout {
    const minWidthPx = opts.minWidthPx ?? NEW_GRID_MIN_CARD_PX;
    const gapMinutes = opts.gapMinutes ?? NEW_GRID_GAP_MINUTES;
    const dayEndMs = dayStartMs + windowHours * 3_600_000;
    let beforeCount = 0;
    let afterCount = 0;
    const timed: Array<{ job: DispatchJob; startMs: number; endMs: number; minutes: number }> = [];
    for (const job of jobs) {
        if (!job.scheduled_start) continue;
        const startMs = Date.parse(job.scheduled_start);
        if (!Number.isFinite(startMs)) continue;
        const minutes = visitMinutes(job);
        const endMs = startMs + minutes * 60_000;
        if (endMs <= dayStartMs) {
            beforeCount++;
            continue;
        }
        if (startMs >= dayEndMs) {
            afterCount++;
            continue;
        }
        timed.push({ job, startMs, endMs, minutes });
    }
    timed.sort((a, b) => a.startMs - b.startMs || a.job.id.localeCompare(b.job.id));

    const blocks: PositionedBlock[] = [];
    const gaps: LaneGap[] = [];
    let prevRight: number | null = null;
    let prevEndMs: number | null = null;
    for (const t of timed) {
        const clockLeft: number = ((Math.max(t.startMs, dayStartMs) - dayStartMs) / 60_000) * pxPerMin;
        const left: number = prevRight == null ? clockLeft : Math.max(clockLeft, prevRight + NEW_GRID_CARD_GAP_PX);
        const widthPx = cardWidthPx(t.minutes, pxPerMin, minWidthPx);
        if (prevRight != null && prevEndMs != null) {
            const openMinutes = (t.startMs - prevEndMs) / 60_000;
            const gapPx = left - prevRight;
            if (openMinutes >= gapMinutes && gapPx > 2 * NEW_GRID_CARD_GAP_PX) {
                gaps.push({
                    leftPx: prevRight + NEW_GRID_CARD_GAP_PX,
                    widthPx: gapPx - 2 * NEW_GRID_CARD_GAP_PX,
                    minutes: Math.round(openMinutes),
                });
            }
        }
        blocks.push({
            job: t.job,
            leftPx: left,
            widthPx,
            startMs: t.startMs,
            endMs: t.endMs,
            minutes: t.minutes,
            clippedLeft: t.startMs < dayStartMs,
            clippedRight: t.endMs > dayEndMs,
            pushedMinutes: pxPerMin > 0 ? (left - clockLeft) / pxPerMin : 0,
        });
        prevRight = left + widthPx;
        prevEndMs = Math.max(prevEndMs ?? 0, t.endMs);
    }
    return { blocks, gaps, contentRight: prevRight ?? 0, beforeCount, afterCount };
}

/**
 * Open stretches of the working day worth offering ("room for about N
 * tests"): before the first stop, between stops, and after the last, each at
 * least `minMinutes` long. In clock pixels, clamped past any card already
 * drawn there.
 */
export function openRooms(
    layout: LaneLayout,
    dayStartMs: number,
    windowHours: number,
    pxPerMin: number,
    minMinutes = 90,
): LaneGap[] {
    const rooms: LaneGap[] = [];
    const dayEndMs = dayStartMs + windowHours * 3_600_000;
    let cursorMs = dayStartMs;
    let cursorPx = 0;
    const push = (fromMs: number, toMs: number, fromPx: number) => {
        const minutes = (toMs - fromMs) / 60_000;
        if (minutes < minMinutes) return;
        const toPx = ((toMs - dayStartMs) / 60_000) * pxPerMin;
        const leftPx = Math.max(fromPx, ((fromMs - dayStartMs) / 60_000) * pxPerMin) + NEW_GRID_CARD_GAP_PX;
        const widthPx = toPx - leftPx - NEW_GRID_CARD_GAP_PX;
        if (widthPx < NEW_GRID_MIN_CARD_PX) return;
        rooms.push({ leftPx, widthPx, minutes: Math.round(minutes) });
    };
    for (const b of layout.blocks) {
        push(cursorMs, b.startMs, cursorPx);
        cursorMs = Math.max(cursorMs, b.endMs);
        cursorPx = b.leftPx + b.widthPx;
    }
    push(cursorMs, dayEndMs, cursorPx);
    return rooms;
}
